import { useState } from 'react';
import { type Customer } from '@hungryroot/customer';
import {
  FoodProfileDisclosure,
  FutureOrdersWillChangeModal,
  ControlTagSwitch,
  type ControlTagSwitchWarning,
  type ControlTagSwitchWarningFunction,
} from '@hungryroot/page-food-profile';
import { useTagGroup } from '@hungryroot/rest-hooks';
import { useSubscriptionWarning } from '@hungryroot/supplements';
import { tagGroups, tags, useCustomerTagGroup } from '@hungryroot/tags';
import { Paragraph, Heading, Button, Nutrition } from '@hungryroot/ui-kit';
import { getCustomerTags } from '../../utils/getCustomerTags';
import { ThresholdModal } from '../ThresholdModal/ThresholdModal';
import styles from './NutritionSection.module.css';

interface Props {
  testid?: string;
  customerId: Customer['id'];
}

export function NutritionSection(props: Props) {
  const { testid, customerId } = props;
  const [showThresholdModal, setShowThresholdModal] = useState<
    (() => void) | null
  >(null);
  const [showHardConstrainModal, setShowHardConstrainModal] = useState<
    (() => void) | null
  >(null);

  const subscriptionWarningCopy = useSubscriptionWarning();

  const { data: customerTagGroupData } = useCustomerTagGroup(
    tagGroups.nutrition
  );

  // Fetches tags from API so we can compare them to the customer's preferences when rendering `subtitle`
  const tagGroup = useTagGroup(tagGroups.nutrition);
  const { data: tagGroupData } = tagGroup || {};
  const queryTags = tagGroupData?.tags;

  const subtitle = getCustomerTags(customerTagGroupData, queryTags);

  const onThresholdReached: ControlTagSwitchWarningFunction = (params) => {
    const { onConfirm } = params;
    setShowThresholdModal(() => {
      return () => {
        onConfirm();
        setShowThresholdModal(null);
      };
    });
  };

  const onLowSodiumHardConstraintSelected: ControlTagSwitchWarningFunction = (
    params
  ) => {
    const { onConfirm } = params;
    setShowHardConstrainModal(() => {
      return () => {
        onConfirm();
        setShowHardConstrainModal(null);
      };
    });
  };

  const lowSodiumWarning: ControlTagSwitchWarning = (args) => {
    const showOnSoftConstraint =
      args.tag.id === tags.LowSodium &&
      Boolean(args.selectedMap?.[tags.OnlyLowSodium]);

    return {
      show: args.tag.id === tags.OnlyLowSodium || showOnSoftConstraint,
      fn: onLowSodiumHardConstraintSelected,
    };
  };

  const thresholdWarning: ControlTagSwitchWarning = (args) => {
    const threshold = 2;
    // show when the count is greater than or equal to the threshold, and the tag is being selected, and the tag is not a hard constraint
    const show =
      args.count >= threshold &&
      args.selected &&
      args.tag.id !== tags.OnlyLowSodium;

    return {
      show: show,
      fn: onThresholdReached,
    };
  };

  const warnings = [lowSodiumWarning, thresholdWarning];

  return (
    <section data-testid={testid} className={styles.container}>
      <FoodProfileDisclosure
        testid={`${testid}-disclosure`}
        title="Nutrition"
        subtitle={subtitle || 'Set your preferences.'}
        icon={<Nutrition />}
        deepLink="nutrition-preferences"
      >
        <Paragraph className={styles.text}>
          Your preferences help us suggest more recipes to fit how you eat.
        </Paragraph>
        <ControlTagSwitch
          warnings={warnings}
          testid={`${testid}-control`}
          customerId={customerId}
          colCount={2}
          tagGroupId={tagGroups.nutrition}
        />
      </FoodProfileDisclosure>

      {showHardConstrainModal ? (
        <FutureOrdersWillChangeModal
          subscriptionWarningCopy={subscriptionWarningCopy}
          testid={testid}
          toggleDialog={() => setShowHardConstrainModal(null)}
          onConfirmUpdate={() => {
            if (showHardConstrainModal) {
              showHardConstrainModal();
            }
          }}
        />
      ) : null}

      {showThresholdModal !== null ? (
        <ThresholdModal
          testid={`${testid}-modal`}
          ariaLabel="dietary-needs-update-modal"
        >
          <div data-testid={`${testid}-modal-body`} className={styles.modal}>
            <Heading variant="H5">Heads up!</Heading>
            <Paragraph>
              We do our best to make sure there are recipe options for everyone.
              Please note that the more nutritional preferences you select, the
              less variety you'll see in future deliveries.
            </Paragraph>
            <div className={styles['modal-buttons']}>
              <Button
                style="muted"
                prominence="secondary"
                size="small"
                onClick={() => {
                  setShowThresholdModal(null);
                }}
                testid={`${testid}-cancel-button`}
              >
                CANCEL
              </Button>
              <Button
                style="muted"
                prominence="primary"
                size="small"
                onClick={showThresholdModal}
                testid={`${testid}-confirm-button`}
              >
                GOT IT
              </Button>
            </div>
          </div>
        </ThresholdModal>
      ) : null}
    </section>
  );
}
